<template>
   <v-container>
    <v-row>
      <v-col cols="3">
        <v-select id="taxLevyEditDialogTaxYearsSelect"
          class="tax-levy-edit-dialog-tax-years-select"
          v-model="editedItem.taxYear"
          :items="taxYearRange"
          readonly
          label="Tax Years"
          @blur="somethingChanged"
          :tabindex="!isNewItem && !isSuperAdmin ? -1 : 0"
        ></v-select>
      </v-col>
      <v-col cols="4">
        <v-select id="taxLevyEditDialogToDistrictSelect"
          class="tax-levy-edit-dialog-to-district-select tax-levy-hint"
          v-model="editedItem.taxDistrict"
          label="To District"
          hint="School district this levy belongs to. Records in this district are charged this levy."
          persistent-hint
          :items="districtList"
          item-text="districtDesc"
          item-value="_id"
          @blur="somethingChanged"
          readonly
          :tabindex="!isNewItem && !isSuperAdmin ? -1 : 0"
        >
        </v-select>
      </v-col>
      <v-col cols="4">
        <v-autocomplete id="taxLevyEditDialogToFundSelect"
          class="tax-levy-edit-dialog-to-fund-select tax-levy-hint"
          v-model="editedItem.taxFund"
          label="To Fund"
          hint="Tax fund this levy pays into (General, Sinking, Building, etc.). Choosing it fills in the default mills. Type a fund number or description to filter."
          persistent-hint
          :items="taxFundList"
          item-text="fundDesc"
          item-value="_id"
          :filter="taxFundFilter"
          @blur="somethingChanged"
          @change="prefillMills"
          autofocus
          :readonly="!isNewItem && !isSuperAdmin"
          :tabindex="!isNewItem && !isSuperAdmin ? -1 : 0"
        >
        </v-autocomplete>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="4" v-if="!this.globalSettingItem.useNewTaxUnitSetup">
        <v-select id="taxLevyEditDialogToUnitSelect"
          class="tax-levy-edit-dialog-to-unit-select tax-levy-hint"
          v-model="editedItem.taxUnit"
          label="Tax Unit"
          hint="Taxing entity (county, school, city, CareerTech, etc.) that receives this money in apportionment."
          persistent-hint
          :items="toUnitDescList"
          item-text="toUnitDesc"
          item-value="_id"
          @blur="somethingChanged"
          :readonly="!isNewItem && !isSuperAdmin"
          :tabindex="!isNewItem && !isSuperAdmin ? -1 : 0"
        >
        </v-select>
      </v-col>
      <v-col cols="4" v-if="this.globalSettingItem.useNewTaxUnitSetup">
        <v-select id="taxFundEditDialogToUnitSelect"
          class="tax-fund-edit-dialog-to-unit-select tax-levy-hint"
          v-model="editedItem.toUnitDescForAppro"
          label="To Unit"
          hint="Taxing entity (county, school, city, CareerTech, etc.) that receives this money in apportionment."
          persistent-hint
          :items="toUnitDescList"
          item-text="toUnitDesc"
          item-value="toUnitDescForAppro"
          @blur="somethingChanged"
          :readonly="!isNewItem && !isSuperAdmin"
          :tabindex="!isNewItem && !isSuperAdmin ? -1 : 0"
        >
        </v-select>
      </v-col>
      <v-col cols="4">
        <v-autocomplete id="taxLevyEditDialogSharedFundSelect"
          class="tax-levy-edit-dialog-shared-fund-select tax-levy-hint"
          v-model="editedItem.fund"
          label="Shared Fund"
          hint="County ledger (GL) fund that collections for this levy post to. Type a fund number or description to filter."
          persistent-hint
          :items="sharedFundList"
          item-text="sharedFundDesc"
          item-value="_id"
          :filter="sharedFundFilter"
          :readonly="!isNewItem && !isSuperAdmin"
          :tabindex="!isNewItem && !isSuperAdmin ? -1 : 0"
        >
        </v-autocomplete>
      </v-col>
      <v-col cols="3">
        <v-text-field id="taxLevyEditDialogMillsText"
          class="tax-levy-edit-dialog-mills-text"
          v-uppercase=globalSettingItem.conEverythingToCaps
          v-model="editedItem.mills"
          label="Mills"
          @blur="somethingChanged"
        ></v-text-field>
      </v-col>
      <v-col cols="3">
        <v-checkbox id="taxLevyEditDialog4MillsCheckbox"
          class="tax-levy-edit-dialog-4-mills-checkbox"
          v-model="editedItem.is4Mill"
          :disabled="!isNewItem && !isSuperAdmin"
          label="4 Mill"
        >
        </v-checkbox>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import {
  getFYList,
} from '../../../util/shared/tmc-global';
import {
  extractId,
  fundSearchFilter,
} from '../../../util/shared/vue-global';
import GlobalToCaps from '../../../util/shared/GlobalToCaps.mixin.vue';

export default {
  name: 'taxLevyEditedItem',
  mixins: [GlobalToCaps],
  components: {
  },
  data: () => ({
    taxUnitCrudKey: 'sharedTaxUnit',
    taxDistrictCrudKey: 'sharedTaxDistrict',
    taxFundCrudKey: 'sharedTaxFund',
    sharedFundCrudKey: 'sharedFund',
    // Fund numbers are typed with or without their leading zeros ("470" finds "0470").
    sharedFundFilter: fundSearchFilter({ numberOf: (f) => f.fund }),
    taxFundFilter: fundSearchFilter({ numberOf: (f) => f.fundNumber }),
  }),
  props: {
    baseCrudKey: {
      type: String,
      default: 'taxLevy-dialog',
    },
    isSuperAdmin: {
      type: Boolean,
      default: false,
    },
  },
  created() {
  },
  computed: {
    ...mapGetters('base/crud', [
      'item',
      'items',
      'index',
      'isVisible',
    ]),
    ...mapState({
      taxYearRange: (state) => {
        const registerdYears = state.shared.taxLevy.regYears || {};
        return getFYList(registerdYears);
      },
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem,
    }),
    editedItem: {
      get() {
        return this.item(this.baseCrudKey);
      },
      set(value) {
        this.setItem([this.baseCrudKey, value]);
      },
    },
    isNewItem() {
      return this.index(this.baseCrudKey) === -1;
    },
    districtList() {
      const districtDescObj = this.items(this.taxDistrictCrudKey) || [];
      const updatedDistDescObj = districtDescObj.map((d) => ({
        ...d,
        districtDesc: `${d.districtCode || ''}  ${d.districtName || ''}`,
      }));
      return [{ _id: null, districtCode: '', districtDesc: '' }, ...updatedDistDescObj];
    },
    toUnitDescList() {
      const unitDescObj = this.items(this.taxUnitCrudKey) || [];
      const updatedUnitDescObj = unitDescObj.map((u) => ({
        ...u,
        toUnitDesc: `${u.toUnitDescForAppro || ''}  ${u.description || ''}`,
      }));
      return [{ _id: null, toUnitDescForAppro: '', toUnitDesc: '' }, ...updatedUnitDescObj];
    },
    taxFundList() {
      const fundDescObj = this.items(this.taxFundCrudKey) || [];
      const updatedFundDescObj = fundDescObj.map((f) => ({
        ...f,
        fundDesc: `${f.fundNumber || ''}  ${f.description || ''}`,
      }));
      return [{ _id: null, fundNumber: '', fundDesc: '' }, ...updatedFundDescObj];
    },
    sharedFundList() {
      /*
        Shared funds are loaded for every fiscal year, so the same fund shows up once per
        FY - and a fund that was renamed along the way shows up with several different
        descriptions too. Offer the latest fiscal year only, so each fund appears once.
      */
      const allFunds = this.items(this.sharedFundCrudKey) || [];
      const latestFiscalYear = allFunds.reduce(
        (max, f) => ((f.fiscalYear || 0) > max ? f.fiscalYear : max), 0,
      );
      const toOption = (f, withFiscalYear = false) => ({
        ...f,
        sharedFundDesc: `${f.fund || ''}  ${f.description || ''}${withFiscalYear ? `  (FY${f.fiscalYear})` : ''}`,
      });
      /* fiscalYear is not required on the fund schema - never narrow the list down to nothing */
      const latestFunds = latestFiscalYear
        ? allFunds.filter((f) => f.fiscalYear === latestFiscalYear)
        : allFunds;
      const options = latestFunds.map((f) => toOption(f));
      /*
        Existing levies point at whichever fiscal year's fund was current when they were
        entered, which is usually not the latest one. Carry that record into the list -
        tagged with its fiscal year - so the levy still displays and saves as it is.
      */
      const selectedFundId = extractId((this.editedItem || {}).fund);
      if (selectedFundId && !latestFunds.some((f) => f._id === selectedFundId)) {
        const selectedFund = allFunds.find((f) => f._id === selectedFundId);
        if (selectedFund) options.push(toOption(selectedFund, true));
      }
      return [{ _id: null, fund: '', sharedFundDesc: '' }, ...options];
    },
  },
  methods: {
    ...mapMutations('base/crud', [
      'setItem',
    ]),
    somethingChanged() {
      this.$emit('input', this.editedItem);
    },
    prefillMills() {
      if (this.isNewItem) {
        const selectedFund = this.taxFundList.find(
          (f) => (this.editedItem.taxFund) === extractId(f),
        );
        if (selectedFund.defaultMills !== null) {
          this.editedItem.mills = selectedFund.defaultMills;
        }
      }
      return this.editedItem.mills;
    },
  },
};
</script>

<style scoped>
.tax-levy-hint ::v-deep .v-messages__message {
  background: #fff8c4;
  border-left: 3px solid #f9a825;
  border-radius: 0 3px 3px 0;
  color: rgba(0, 0, 0, 0.75);
  padding: 4px 10px;
  margin-top: 4px;
  line-height: 1.35;
}
</style>
